import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Package, Scan, Plus, RotateCcw, Trash2, Search, Camera, X, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import BarcodeScanner from '../components/BarcodeScanner';
import CustomerCombobox from '../components/CustomerCombobox';
import { rowKey } from '../lib/rowKey';

const equipmentTypes = [
  "Tappetårn",
  "Kjøler",
  "Fat",
  "CO2-regulator",
  "Slanger",
  "Koblinger",
  "Rullebar",
  "Bordmodell",
  "Søyle-anlegg",
  "Gassflaske",
  "Annet"
];

export const LoanedEquipment = () => {
  const { user } = useContext(AuthContext);
  const [activeLoans, setActiveLoans] = useState([]);
  const [returnedLoans, setReturnedLoans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newLoanDialog, setNewLoanDialog] = useState(false);
  const [editLoanDialog, setEditLoanDialog] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  
  const [scanInput, setScanInput] = useState('');
  const [returnDialog, setReturnDialog] = useState(null);
  const [returnNotes, setReturnNotes] = useState('');
  const [newLoan, setNewLoan] = useState({
    customer_id: '',
    customer_name: '',
    use_manual_customer: true,
    notes: '',
    items: [{ _key: rowKey(), equipment_type: '', serial_number: '' }]
  });
  const [editLoan, setEditLoan] = useState(null);
  
  const scanInputRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Cleanup camera on unmount (ref holds the scanner instance, so reading the latest value is intended)
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        html5QrCodeRef.current.stop().catch((err) => console.warn('[LoanedEquipment]', err));
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const [activeRes, returnedRes, customersRes] = await Promise.all([
        axios.get(`${API}/loaned-equipment?status=active`),
        axios.get(`${API}/loaned-equipment?status=returned`),
        axios.get(`${API}/customers`)
      ]);
      setActiveLoans(activeRes.data);
      setReturnedLoans(returnedRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const lookupEquipment = async (serialNumber) => {
    try {
      const response = await axios.get(`${API}/loaned-equipment/lookup/${serialNumber}`);
      return response.data;
    } catch {
      return { found: false };
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;
    
    try {
      const response = await axios.get(`${API}/loaned-equipment/scan/${scanInput.trim()}`);
      setScanResult(response.data);
      
      if (response.data.found) {
        toast.info(`Utstyr funnet: Utlånt til ${response.data.loan.customer_name}`);
      } else {
        // Nytt utlån - forhåndsutfyll serienummer og eventuelt type på første linje
        setNewLoan(prev => ({
          ...prev,
          items: [{ _key: rowKey(), equipment_type: response.data.known_equipment_type || '', serial_number: scanInput.trim() }]
        }));
        setNewLoanDialog(true);
        
        if (response.data.known_equipment_type) {
          toast.info(`Kjent utstyr: ${response.data.known_equipment_type}`);
        } else {
          toast.info('Nytt serienummer - registrer utlån');
        }
      }
      setScanInput('');
    } catch (error) {
      toast.error('Feil ved skanning');
    }
  };

  const updateItem = (index, field, value) => {
    setNewLoan(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const updateItemSerial = async (index, value) => {
    updateItem(index, 'serial_number', value);
    if (value.length >= 3) {
      const lookup = await lookupEquipment(value);
      if (lookup.found) {
        setNewLoan(prev => {
          const items = [...prev.items];
          if (!items[index].equipment_type) {
            items[index] = { ...items[index], equipment_type: lookup.equipment_type };
          }
          return { ...prev, items };
        });
      }
    }
  };

  const addLoanItem = () => setNewLoan(prev => ({ ...prev, items: [...prev.items, { _key: rowKey(), equipment_type: '', serial_number: '' }] }));
  const removeLoanItem = (index) => setNewLoan(prev => ({
    ...prev,
    items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : prev.items
  }));

  const lookupScannedSerial = async (serial) => {
    const code = (serial || '').trim();
    if (!code) return;
    setCameraOpen(false);
    setScanInput(code);
    try {
      const response = await axios.get(`${API}/loaned-equipment/scan/${code}`);
      setScanResult(response.data);
      if (response.data.found) {
        toast.success(`Skannet: Utlånt til ${response.data.loan.customer_name}`);
      } else {
        setNewLoan(prev => ({ ...prev, items: [{ _key: rowKey(), equipment_type: response.data.known_equipment_type || '', serial_number: code }] }));
        setNewLoanDialog(true);
        toast.success('Skannet: ' + code);
      }
    } catch (error) {
      toast.error('Feil ved oppslag');
    }
  };

  const handleReturnFromScan = async () => {
    if (!scanResult?.loan) return;
    setReturnDialog({ mode: 'single', id: scanResult.loan.id });
    setReturnNotes('');
    setScanResult(null);
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();

    const hasCustomer = newLoan.use_manual_customer
      ? newLoan.customer_name.trim()
      : newLoan.customer_id;

    if (!hasCustomer) {
      toast.error('Velg eller skriv inn kunde');
      return;
    }

    const validItems = newLoan.items.filter(it => it.equipment_type && it.serial_number.trim());
    if (!validItems.length) {
      toast.error('Legg til minst én linje med type og serienummer');
      return;
    }

    try {
      const payload = {
        items: validItems.map(it => ({ equipment_type: it.equipment_type, serial_number: it.serial_number.trim() })),
        notes: newLoan.notes || null
      };

      if (newLoan.use_manual_customer) {
        payload.customer_name = newLoan.customer_name;
      } else {
        payload.customer_id = newLoan.customer_id;
      }

      await axios.post(`${API}/loaned-equipment/batch`, payload);
      toast.success(validItems.length > 1 ? `${validItems.length} utlån registrert!` : 'Utlån registrert!');
      setNewLoanDialog(false);
      setNewLoan({ customer_id: '', customer_name: '', use_manual_customer: true, notes: '', items: [{ _key: rowKey(), equipment_type: '', serial_number: '' }] });
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke registrere utlån');
    }
  };

  const handleEditLoan = async (e) => {
    e.preventDefault();
    if (!editLoan) return;
    
    try {
      const payload = {
        equipment_type: editLoan.equipment_type,
        serial_number: editLoan.serial_number,
        notes: editLoan.notes
      };
      
      if (editLoan.use_manual_customer) {
        payload.customer_name = editLoan.customer_name;
      } else if (editLoan.customer_id) {
        payload.customer_id = editLoan.customer_id;
      }
      
      await axios.put(`${API}/loaned-equipment/${editLoan.id}`, payload);
      toast.success('Utlån oppdatert!');
      setEditLoanDialog(false);
      setEditLoan(null);
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke oppdatere utlån');
    }
  };

  const handleReturn = async (loanId) => {
    setReturnDialog({ mode: 'single', id: loanId });
    setReturnNotes('');
  };

  const confirmReturn = async () => {
    if (!returnDialog) return;
    try {
      const url = returnDialog.mode === 'group'
        ? `${API}/loaned-equipment/group/${returnDialog.id}/return`
        : `${API}/loaned-equipment/${returnDialog.id}/return`;
      await axios.put(url, { return_notes: returnNotes || null });
      toast.success('Utstyr returnert!');
      setReturnDialog(null);
      setReturnNotes('');
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke registrere retur');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/loaned-equipment/${deleteId}`);
      toast.success('Utlån slettet');
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke slette utlån');
    }
  };

  const openEditDialog = (loan) => {
    const hasCustomerId = loan.customer_id && loan.customer_id.length > 0;
    setEditLoan({
      id: loan.id,
      equipment_type: loan.equipment_type,
      serial_number: loan.serial_number,
      customer_id: loan.customer_id || '',
      customer_name: loan.customer_name || '',
      use_manual_customer: !hasCustomerId,
      notes: loan.notes || ''
    });
    setEditLoanDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd. MMM yyyy HH:mm', { locale: nb });
    } catch {
      return dateStr;
    }
  };

  const groupLoans = (loans) => Object.values(loans.reduce((acc, loan) => {
    const gid = loan.loan_group_id || loan.id;
    (acc[gid] = acc[gid] || { gid, items: [] }).items.push(loan);
    return acc;
  }, {}));
  const groupedActive = groupLoans(activeLoans);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Utlånt Utstyr
        </h1>
        <p className="text-slate-600">Administrer utlån og retur av utstyr</p>
      </div>

      {/* Kamera skanner modal */}
      {cameraOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Skann Strekkode</h3>
              <Button variant="ghost" size="sm" onClick={() => setCameraOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <BarcodeScanner onScan={lookupScannedSerial} onClose={() => setCameraOpen(false)} />
          </div>
        </div>
      )}

      {/* Skanne-seksjon */}
      <Card className="mb-6 border-brand-200 bg-brand-50/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Scan className="h-5 w-5 text-brand-600" />
            <CardTitle className="text-brand-800">Skann Utstyr</CardTitle>
          </div>
          <CardDescription className="text-brand-600">
            Skann strekkode eller skriv inn serienummer for rask registrering
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleScan} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                ref={scanInputRef}
                placeholder="Skann eller skriv serienummer..."
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                className="text-lg h-12"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="h-12 px-6 bg-brand-600 hover:bg-brand-700 flex-1 sm:flex-none">
                <Search className="h-5 w-5 mr-2" />
                Søk
              </Button>
              <Button 
                type="button" 
                onClick={() => setCameraOpen(true)}
                className="h-12 px-6 bg-brand-600 hover:bg-brand-700 flex-1 sm:flex-none"
              >
                <Camera className="h-5 w-5 mr-2" />
                Kamera
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Skanne-resultat dialog */}
      {scanResult?.found && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-800">Utstyr funnet - Registrer retur?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p><strong>Type:</strong> {scanResult.loan.equipment_type}</p>
              <p className="break-all"><strong>Serienummer:</strong> {scanResult.loan.serial_number}</p>
              <p><strong>Utlånt til:</strong> {scanResult.loan.customer_name}</p>
              <p><strong>Utlånt:</strong> {formatDate(scanResult.loan.loaned_at)}</p>
              <p><strong>Utlevert av:</strong> {scanResult.loan.loaned_by_name}</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleReturnFromScan} className="bg-green-600 hover:bg-green-700">
                <RotateCcw className="h-4 w-4 mr-2" />
                Registrer Retur
              </Button>
              <Button variant="outline" onClick={() => setScanResult(null)}>
                Avbryt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Handlinger */}
      <div className="flex gap-3 mb-6">
        <Dialog open={newLoanDialog} onOpenChange={setNewLoanDialog}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-2" />
              Nytt Utlån
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrer Nytt Utlån</DialogTitle>
              <DialogDescription>Fyll ut informasjon om utstyret som lånes ut</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateLoan} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Utstyr *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLoanItem} data-testid="add-loan-item">
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Legg til linje
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Skriv inn serienummer for å auto-fylle kjent utstyrstype. Du kan legge til flere linjer i ett utlån.</p>
                {newLoan.items.map((item, index) => (
                  <div key={item._key || index} className="p-3 border border-slate-200 rounded-lg space-y-2 bg-slate-50" data-testid={`loan-item-${index}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-500">Linje {index + 1}</span>
                      {newLoan.items.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLoanItem(index)} className="h-6 w-6 p-0 text-red-500" data-testid={`remove-loan-item-${index}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <Input
                      value={item.serial_number}
                      onChange={(e) => updateItemSerial(index, e.target.value)}
                      placeholder="Serienummer *"
                      data-testid={`loan-item-serial-${index}`}
                    />
                    <Select value={item.equipment_type} onValueChange={(value) => updateItem(index, 'equipment_type', value)}>
                      <SelectTrigger data-testid={`loan-item-type-${index}`}>
                        <SelectValue placeholder="Velg type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {equipmentTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Kunde *</Label>
                  <button
                    type="button"
                    data-testid="new-loan-customer-mode-toggle"
                    onClick={() => setNewLoan({ ...newLoan, use_manual_customer: !newLoan.use_manual_customer, customer_id: '', customer_name: '' })}
                    className="text-xs text-brand-600 hover:text-brand-700 underline"
                  >
                    {newLoan.use_manual_customer ? 'Velg fra liste' : 'Skriv manuelt'}
                  </button>
                </div>
                {newLoan.use_manual_customer ? (
                  <Input
                    placeholder="Skriv inn kundenavn..."
                    value={newLoan.customer_name}
                    onChange={(e) => setNewLoan({ ...newLoan, customer_name: e.target.value })}
                  />
                ) : (
                  <CustomerCombobox
                    customers={customers}
                    value={newLoan.customer_id}
                    onChange={(value) => setNewLoan({ ...newLoan, customer_id: value })}
                    placeholder="Søk og velg kunde..."
                    testId="new-loan-customer-select"
                  />
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notater</Label>
                <Textarea
                  id="notes"
                  value={newLoan.notes}
                  onChange={(e) => setNewLoan({ ...newLoan, notes: e.target.value })}
                  placeholder="Eventuelle notater..."
                  rows={2}
                />
              </div>
              
              <div className="text-sm text-slate-500">
                Utlevert av: <strong>{user?.name}</strong> | Dato: <strong>{format(new Date(), 'dd. MMM yyyy', { locale: nb })}</strong>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setNewLoanDialog(false)} className="flex-1">
                  Avbryt
                </Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                  Registrer Utlån
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editLoanDialog} onOpenChange={setEditLoanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rediger Utlån</DialogTitle>
            <DialogDescription>Oppdater informasjon om utlånet</DialogDescription>
          </DialogHeader>
          {editLoan && (
            <form onSubmit={handleEditLoan} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_serial_number">Serienummer *</Label>
                <Input
                  id="edit_serial_number"
                  value={editLoan.serial_number}
                  onChange={(e) => setEditLoan({ ...editLoan, serial_number: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type Utstyr *</Label>
                <Select
                  value={editLoan.equipment_type}
                  onValueChange={(value) => setEditLoan({ ...editLoan, equipment_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Velg type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {equipmentTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Kunde *</Label>
                  <button
                    type="button"
                    onClick={() => setEditLoan({ ...editLoan, use_manual_customer: !editLoan.use_manual_customer, customer_id: '', customer_name: editLoan.customer_name || '' })}
                    className="text-xs text-brand-600 hover:text-brand-700 underline"
                  >
                    {editLoan.use_manual_customer ? 'Velg fra liste' : 'Skriv manuelt'}
                  </button>
                </div>
                {editLoan.use_manual_customer ? (
                  <Input
                    placeholder="Skriv inn kundenavn..."
                    value={editLoan.customer_name}
                    onChange={(e) => setEditLoan({ ...editLoan, customer_name: e.target.value })}
                  />
                ) : (
                  <CustomerCombobox
                    customers={customers}
                    value={editLoan.customer_id}
                    onChange={(value) => setEditLoan({ ...editLoan, customer_id: value })}
                    placeholder="Søk og velg kunde..."
                    testId="edit-loan-customer-select"
                  />
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_notes">Notater</Label>
                <Textarea
                  id="edit_notes"
                  value={editLoan.notes}
                  onChange={(e) => setEditLoan({ ...editLoan, notes: e.target.value })}
                  placeholder="Eventuelle notater..."
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditLoanDialog(false)} className="flex-1">
                  Avbryt
                </Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                  Lagre Endringer
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Tabs for aktive og returnerte */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Aktive Utlån ({activeLoans.length})
          </TabsTrigger>
          <TabsTrigger value="returned" className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            Returnert ({returnedLoans.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          {activeLoans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Ingen aktive utlån</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {groupedActive.map((group) => {
                const first = group.items[0];
                return (
                  <Card key={group.gid} className="hover:shadow-md transition-shadow" data-testid={`loan-group-${group.gid}`}>
                    <CardContent className="py-4">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap text-sm text-slate-600">
                            <span className="font-semibold text-slate-900">{first.customer_name}</span>
                            <span className="text-slate-300">·</span>
                            <span>{formatDate(first.loaned_at)}</span>
                            <span className="text-slate-300">·</span>
                            <span>Utlevert av {first.loaned_by_name}</span>
                            {group.items.length > 1 && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">{group.items.length} enheter</span>
                            )}
                          </div>
                          <div className="space-y-2">
                            {group.items.map((loan) => (
                              <div key={loan.id} className="flex items-center justify-between gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100" data-testid={`loan-item-row-${loan.id}`}>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-slate-900">{loan.equipment_type}</span>
                                    <span className="px-2 py-0.5 text-xs font-medium bg-brand-100 text-brand-700 rounded-xl break-all">{loan.serial_number}</span>
                                  </div>
                                  {loan.notes && <p className="text-xs text-slate-400 italic mt-0.5">{loan.notes}</p>}
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                  <Button size="sm" variant="outline" onClick={() => openEditDialog(loan)} className="text-slate-600 h-9 w-9 sm:h-7 sm:w-auto px-2">
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="sm" onClick={() => handleReturn(loan.id)} className="bg-green-600 hover:bg-green-700 h-9 sm:h-7 min-w-9 text-xs px-2" data-testid={`return-item-${loan.id}`}>
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    <span className="ml-1 hidden sm:inline">Returner</span>
                                  </Button>
                                  {(user?.role === 'admin' || user?.role === 'system_admin') && (
                                    <Button size="sm" variant="ghost" onClick={() => setDeleteId(loan.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 w-9 sm:h-7 sm:w-auto px-1.5">
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {group.items.length > 1 && (
                          <div className="shrink-0">
                            <Button size="sm" onClick={() => { setReturnDialog({ mode: 'group', id: group.gid }); setReturnNotes(''); }} className="bg-green-700 hover:bg-green-800" data-testid={`return-group-${group.gid}`}>
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Returner alt
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="returned">
          {returnedLoans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <RotateCcw className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Ingen returnerte utlån</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {returnedLoans.map((loan) => (
                <Card key={loan.id} className="bg-slate-50">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-bold text-lg text-slate-700">{loan.equipment_type}</span>
                          <span className="px-2 py-0.5 text-xs font-medium bg-slate-200 text-slate-600 rounded-xl break-all">
                            {loan.serial_number}
                          </span>
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            Returnert
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 text-sm text-slate-500">
                          <div>
                            <span className="text-slate-400">Kunde:</span>{' '}
                            {loan.customer_name}
                          </div>
                          <div>
                            <span className="text-slate-400">Utlånt:</span>{' '}
                            {formatDate(loan.loaned_at)}
                          </div>
                          <div>
                            <span className="text-slate-400">Returnert:</span>{' '}
                            {formatDate(loan.returned_at)}
                          </div>
                          <div>
                            <span className="text-slate-400">Utlevert av:</span>{' '}
                            {loan.loaned_by_name}
                          </div>
                        </div>
                        {loan.return_notes && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <span className="text-sm font-medium text-red-700">{loan.return_notes}</span>
                          </div>
                        )}
                      </div>
                      {(user?.role === 'admin' || user?.role === 'system_admin') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(loan.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-4"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Return confirmation dialog with comment */}
      <Dialog open={!!returnDialog} onOpenChange={(open) => { if (!open) { setReturnDialog(null); setReturnNotes(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrer Retur</DialogTitle>
            <DialogDescription>Vil du registrere dette utstyret som returnert?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="return_notes">Kommentar ved retur (valgfritt)</Label>
              <Textarea
                id="return_notes"
                data-testid="return-notes-input"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="F.eks. skadet, mangler deler, etc."
                rows={3}
              />
              {returnNotes && (
                <p className="text-xs text-red-600">Kommentaren vil vises i rødt på returnert-siden</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => { setReturnDialog(null); setReturnNotes(''); }} className="flex-1">
                Avbryt
              </Button>
              <Button onClick={confirmReturn} className="flex-1 bg-green-600 hover:bg-green-700">
                <RotateCcw className="h-4 w-4 mr-2" />
                Bekreft Retur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett utlånsregistrering?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette denne utlånsregistreringen? Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoanedEquipment;
